// src/components/digital_twin/AssetInformationPanel.js
// ============================================================================
// FlowSense Enterprise — Asset Information Panel
// Version: 5.1.0-enterprise (Time-Travel Enabled, Clean Fonts)
//
// ARCHITECTURE:
//  ┌────────────────────────────────────────────────────────────────────────┐
//  │  AssetInformationPanel (Root)                                          │
//  │  ├── useFlowSenseAsset      — enriches asset + fetches linked sensors  │
//  │  ├── useTelemetryFeed       — WS subscription + REST history per device│
//  │  ├── useConditionEngine     — PSI/level severity evaluation            │
//  │  ├── useActionDispatcher    — typed FlowSense API actions              │
//  │  ├── useAssetHealthHistory  — rolling health score trend               │
//  │  ├── useAutoRefresh         — periodic background data refresh         │
//  │  └── View Layers                                                       │
//  │       ├── EmptyState                                                   │
//  │       ├── PanelHeader       — name, ID copy, status, badges, menu     │
//  │       ├── CriticalAlert     — AI failure prediction strip             │
//  │       ├── MaintenanceScheduler — inline form (Collapse)               │
//  │       └── Tabs                                                         │
//  │            ├── OVERVIEW  — primary telemetry, asset details, stats    │
//  │            ├── SENSORS   — SensorRow per linked sensor (live + spark) │
//  │            ├── HEALTH    — AssetHealthPanel + trend + scheduler CTA   │
//  │            └── ACTIVITY  — work orders, alerts, issues (expandable)   │
//  └────────────────────────────────────────────────────────────────────────┘
// ============================================================================
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   FIXES
//   • Every REST call went to /api/v1/api/v1/… (config.apiUrl already ends in /api/v1): sensor
//     history, Schedule Maintenance, Acknowledge alert and Resolve work order all returned 404.
//   • "Refresh Telemetry" waited 800 ms and showed "Telemetry Refreshed" without fetching —
//     it now reloads the asset + sensors and calls the page's `onRefresh`.
//   • Alerts / issues / work orders / predictions were matched with === (string vs number ids),
//     so most never appeared under the asset; the primary sensor likewise.
//   • Sparklines drew newest → oldest (WebSocket readings are prepended) — sorted by time.
//   • Numeric water-level readings were labelled "PSI".
//   • "Open Digital Twin" opened the analytics modal — it opens the twin drawer (`onOpenTwin`).
//   • All sensors were downloaded again on every selection — the page's `sensors` are used.
//   FEATURES (optional props)
//   • FlowSense AI card on Overview: explainable risk score, drivers, recommended actions,
//     sensor anomalies (`riskScore`, `intel`); AssetHealthPanel gets the same.
//   • Sensor rows: "Sensor intelligence" opens the twin's sensor drawer (`onOpenSensor`), ML badge
//     when FlowSense flags the stream, history from the page's live buffer (`historyOf`).
// --- END UPDATE ---------------------------------------------------------------------------

import React, {
  useMemo, useState, useEffect, useCallback, useRef, memo,
} from 'react';
import {
  Box, Heading, Text, VStack, Flex, Badge, Button,
  Alert, AlertIcon, AlertTitle, Icon, useColorModeValue,
  IconButton, useClipboard, Tooltip, HStack, Skeleton,
  Menu, MenuButton, MenuList, MenuItem, Collapse, useDisclosure,
  SimpleGrid, Portal, useToast, Spinner, Tag, Tabs, TabList,
  Tab, TabPanels, TabPanel, Input, Select, Textarea,
  FormControl, FormLabel, Progress, Divider, AlertDescription,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FaTools, FaExclamationTriangle, FaMicrochip, FaBrain,
  FaChartLine, FaFileExport, FaCheckCircle, FaTimesCircle,
  FaWrench, FaServer, FaLayerGroup, FaRobot, FaTint,
  FaWater, FaBolt, FaThermometerHalf, FaMapMarkerAlt,
  FaDatabase, FaClock, FaMinus, FaCheckDouble,
  FaExclamationCircle, FaCalendarAlt, FaNetworkWired,
  FaSignal, FaCity, FaHistory,
} from 'react-icons/fa';
import {
  MdContentCopy, MdCheck, MdExpandMore, MdExpandLess, MdMoreVert,
  MdRefresh, MdNotifications, MdInfo, MdSensors, MdRouter,
  MdDashboard, MdHealthAndSafety, MdEventNote, MdTrendingDown,
  MdTrendingUp, MdTrendingFlat,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

import * as SensorService from '../../services/sensorService';
import * as AssetService  from '../../services/assetService';
import webSocketService   from '../../services/websocketService';
import { getAuthToken }   from '../../utils/auth';
import AssetHealthPanel   from './AssetHealthPanel';
import { config } from '../../config';
// --- UPDATE
import { explainAsset, seriesOf, sensorKey as twinSensorKey } from './twinIntel';
// --- END UPDATE

const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);

const API_BASE_URL = String(config.apiUrl || '').replace(/\/api\/v1\/?$/, ''); // --- UPDATE: config.apiUrl already ends in /api/v1 (calls below add it again)

const HISTORY_LIMIT    = 50;
const READING_STALE_MS = 30_000;
const HEALTH_HISTORY_MAX = 120;

const SENSOR_META = {
  water_pressure: { label: 'Water Pressure', unit: 'PSI',   icon: FaTint,            color: '#22d3ee' },
  pressure:       { label: 'Pressure',       unit: 'PSI',   icon: FaTint,            color: '#22d3ee' },
  flow_rate:      { label: 'Flow Rate',      unit: 'GPM',   icon: FaWater,           color: '#34d399' },
  water_level:    { label: 'Water Level',    unit: '',      icon: FaWater,           color: '#60a5fa' },
  battery_level:  { label: 'Battery',        unit: '%',     icon: FaBolt,            color: '#facc15' },
  temperature:    { label: 'Temperature',    unit: '°C',    icon: FaThermometerHalf, color: '#fb923c' },
  turbidity:      { label: 'Turbidity',      unit: 'NTU',   icon: FaTint,            color: '#a78bfa' },
  pH:             { label: 'pH Level',       unit: 'pH',    icon: FaTint,            color: '#f472b6' },
  chlorine:       { label: 'Chlorine',       unit: 'mg/L',  icon: FaTint,            color: '#86efac' },
};

const sensorMeta = (type) => SENSOR_META[type] ?? SENSOR_META['pressure'];

const FADE_UP = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.26, ease: 'easeOut' } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const STAGGER = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.26, ease: 'easeOut', staggerChildren: 0.05 } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const ROW_SLIDE = {
  hidden:  { opacity: 0, x: -10 },
  visible: (i) => ({ opacity: 1, x: 0, transition: { delay: i * 0.04, duration: 0.18 } }),
};

const livePulse = keyframes`
  0%   { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(52,211,153,0.7); }
  70%  { transform: scale(1);    box-shadow: 0 0 0 7px rgba(52,211,153,0); }
  100% { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(52,211,153,0); }
`;

const staleFlicker = keyframes`
  0%,100% { opacity: 1; }
  50%      { opacity: 0.3; }
`;

const floatAnim = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-7px); }
`;

const useFlowSenseAsset = (asset, refreshSignal, sensorPipelineLinks = {}, knownSensors = null) => { // --- UPDATE: + knownSensors
  const [enriched,      setEnriched]      = useState(null);
  const [linkedSensors, setLinkedSensors] = useState([]);
  const [isLoading,     setIsLoading]     = useState(false);
  const [error,         setError]         = useState(null);
  const isMounted = useRef(true);

  const pipelineLinksStr = JSON.stringify(sensorPipelineLinks);

  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  useEffect(() => {
    if (!asset?.id && !asset?.device_id) { setEnriched(asset); setLinkedSensors([]); return; }

    const load = async () => {
      setIsLoading(true); setError(null);
      try {
        let detail = asset;
        
        if (!asset.isSensor && !asset.isPipeline && typeof AssetService.fetchAssetById === 'function') {
            try { detail = await AssetService.fetchAssetById(asset.id); } catch(e){}
        }
        if (isMounted.current) setEnriched({ ...asset, ...detail });

        const all = Array.isArray(knownSensors) ? knownSensors : await SensorService.fetchSensors(); // --- UPDATE: reuse the page's sensors
        const pipelineLinks = JSON.parse(pipelineLinksStr);

        const linked = (all || []).filter(s => {
            const sId = s.device_id || s.id;
            if (asset.isSensor && String(sId) === String(asset.device_id || asset.id)) return true;
            if (String(s.linked_entity_id) === String(asset.id)) return true;
            if (String(pipelineLinks[sId]) === String(asset.id)) return true;
            return false;
        });

        if (isMounted.current) setLinkedSensors(linked);

      } catch (err) {
        console.error('[AssetInfoPanel] Enrichment error:', err);
        if (isMounted.current) { setError(err.message); setEnriched(asset); }
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, asset?.device_id, refreshSignal, pipelineLinksStr, Array.isArray(knownSensors) ? knownSensors.length : -1]); // --- UPDATE: + sensors

  return { enriched: enriched ?? asset, linkedSensors, isLoading, error };
};

const useTelemetryFeed = (deviceId, sensorType, externalReading, isHistoricalMode = false, historicalTimestamp = null) => {
  const [liveReading, setLiveReading] = useState(null);
  const [history,     setHistory]     = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isStale,     setIsStale]     = useState(false);
  const staleRef = useRef(null);

  const refreshReading = useCallback((val, ts) => {
    setLiveReading(val);
    setLastUpdated(ts ?? Date.now());
    
    if (isHistoricalMode) {
      setIsStale(false);
      clearTimeout(staleRef.current);
    } else {
      setIsStale(false);
      clearTimeout(staleRef.current);
      staleRef.current = setTimeout(() => setIsStale(true), READING_STALE_MS);
    }
  }, [isHistoricalMode]);

  const externalVal = typeof externalReading === 'object'
    ? (externalReading?.psi ?? externalReading?.level ?? externalReading?.value)
    : externalReading;

  useEffect(() => {
    if (externalVal == null) return;
    refreshReading(externalVal, isHistoricalMode ? historicalTimestamp : Date.now());
  }, [externalVal, refreshReading, isHistoricalMode, historicalTimestamp]);

  useEffect(() => {
    if (!deviceId || isHistoricalMode) return;

    const onPressure = (msg) => {
      if (!msg?.payload) return;
      const { device_id, deviceId: dId, psi, pressure, timestamp } = msg.payload;
      if (String(device_id ?? dId) !== String(deviceId)) return;
      const val = psi ?? pressure;
      if (val == null) return;
      refreshReading(val, timestamp ? new Date(timestamp).getTime() : Date.now());
      setHistory(p => [{ psi: val, timestamp: timestamp ?? new Date().toISOString() }, ...p].slice(0, HISTORY_LIMIT));
    };

    const onWaterLevel = (msg) => {
      if (!msg?.payload) return;
      const { device_id, device, level, timestamp } = msg.payload;
      if (String(device_id ?? device) !== String(deviceId)) return;
      refreshReading(level, timestamp ? new Date(timestamp).getTime() : Date.now());
      setHistory(p => [{ level, timestamp: timestamp ?? new Date().toISOString() }, ...p].slice(0, HISTORY_LIMIT));
    };

    webSocketService.subscribe('new_pressure_reading',    onPressure);
    webSocketService.subscribe('new_water_level_reading', onWaterLevel);
    webSocketService.subscribe('psi_update',              onPressure);
    webSocketService.subscribe('telemetry',               onPressure);

    return () => {
      webSocketService.unsubscribe('new_pressure_reading',    onPressure);
      webSocketService.unsubscribe('new_water_level_reading', onWaterLevel);
      webSocketService.unsubscribe('psi_update',              onPressure);
      webSocketService.unsubscribe('telemetry',               onPressure);
      clearTimeout(staleRef.current);
    };
  }, [deviceId, refreshReading, isHistoricalMode]);

  useEffect(() => {
    if (!deviceId || isHistoricalMode) return;
    const token = getAuthToken();
    if (!token) return;
    const url = sensorType === 'water_level'
      ? `${API_BASE_URL}/api/v1/water-level/${deviceId}?limit=${HISTORY_LIMIT}`
      : `${API_BASE_URL}/api/v1/sensors/${deviceId}/readings?limit=${HISTORY_LIMIT}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(json => { if (json?.data?.length) setHistory(json.data.slice(0, HISTORY_LIMIT)); })
      .catch(() => {});
  }, [deviceId, sensorType, isHistoricalMode]);

  return { liveReading, history, lastUpdated, isStale };
};

const useConditionEngine = (reading, psiConditions) => useMemo(() => {
  if (reading == null)
    return { severity: 'none', label: 'No Data', color: '#94a3b8', chakra: 'gray', condition: null };

  if (typeof reading === 'string') {
    const lv = reading.toUpperCase();
    if (lv === 'EMPTY') return { severity: 'critical', label: 'Empty',  color: '#ef4444', chakra: 'red',    condition: null };
    if (lv === 'LOW')   return { severity: 'warning',  label: 'Low',    color: '#f59e0b', chakra: 'yellow', condition: null };
    if (lv === 'HIGH')  return { severity: 'info',     label: 'High',   color: '#22d3ee', chakra: 'cyan',   condition: null };
    if (lv === 'FULL')  return { severity: 'ok',       label: 'Full',   color: '#34d399', chakra: 'green',  condition: null };
    return                     { severity: 'ok',       label: 'Normal', color: '#34d399', chakra: 'green',  condition: null };
  }

  const psi = parseFloat(reading);
  if (isNaN(psi))
    return { severity: 'none', label: 'Invalid', color: '#94a3b8', chakra: 'gray', condition: null };

  if (psiConditions?.length) {
    const match = psiConditions.find(c => psi >= c.min_psi && psi <= c.max_psi);
    if (match) {
      const sev = match.status_label?.toLowerCase() === 'normal' ? 'ok'
        : match.status_label?.toLowerCase().includes('critical') ? 'critical' : 'warning';
      return {
        severity:  sev,
        label:     match.status_label,
        color:     match.color_code || '#34d399',
        chakra:    sev === 'ok' ? 'green' : sev === 'critical' ? 'red' : 'yellow',
        condition: match,
      };
    }
  }

  if (psi < 20 || psi > 100) return { severity: 'critical', label: 'Out of Range', color: '#ef4444', chakra: 'red',    condition: null };
  if (psi < 30 || psi > 90)  return { severity: 'warning',  label: 'Warning',      color: '#f59e0b', chakra: 'yellow', condition: null };
  return                            { severity: 'ok',       label: 'Normal',       color: '#34d399', chakra: 'green',  condition: null };
}, [reading, psiConditions]);

const useAssetHealthHistory = (assetHealth) => {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!assetHealth?.health_score) return;
    const entry = { score: assetHealth.health_score, ts: Date.now() };
    setHistory(prev => [...prev.slice(-(HEALTH_HISTORY_MAX - 1)), entry]);
  }, [assetHealth?.health_score]);

  const trend = useMemo(() => {
    if (history.length < 3) return 'stable';
    const recent = history.slice(-6);
    const delta  = recent[recent.length - 1].score - recent[0].score;
    if (delta < -2) return 'degrading';
    if (delta >  2) return 'recovering';
    return 'stable';
  }, [history]);

  return { history, trend };
};

const useActionDispatcher = (assetId, onComplete) => {
  const [isActing,   setIsActing]   = useState(false);
  const [lastAction, setLastAction] = useState(null);

  const dispatch = useCallback(async (actionType, payload = {}) => {
    setIsActing(true);
    const token = getAuthToken();
    let result;
    try {
      switch (actionType) {
        case 'SCHEDULE_MAINTENANCE': {
          const r = await fetch(`${API_BASE_URL}/api/v1/assets/${assetId}/maintenance`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          result = { action: actionType, success: r.ok, statusCode: r.status };
          break;
        }
        case 'ACKNOWLEDGE_ALERT': {
          const r = await fetch(`${API_BASE_URL}/api/v1/alerts/${payload.alertId}/acknowledge`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          result = { action: actionType, success: r.ok, statusCode: r.status };
          break;
        }
        case 'RESOLVE_WORK_ORDER': {
          const r = await fetch(`${API_BASE_URL}/api/v1/work-orders/${payload.workOrderId}/resolve`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          result = { action: actionType, success: r.ok, statusCode: r.status };
          break;
        }
        default:
          result = { action: actionType, success: false, error: 'Unknown action type' };
      }
    } catch (err) {
      result = { action: actionType, success: false, error: err.message };
    } finally {
      result.ts = Date.now();
      setLastAction(result);
      onComplete?.(result);
      setIsActing(false);
    }
  }, [assetId, onComplete]);

  return { dispatch, isActing, lastAction };
};

const useAutoRefresh = (ms = 0) => {
  const [signal, setSignal] = useState(0);
  useEffect(() => {
    if (!ms || ms <= 0) return;
    const id = setInterval(() => setSignal(s => s + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return signal;
};

const MiniSparkline = memo(({ data = [], color = '#22d3ee', height = 32 }) => {
  const vals = useMemo(() => {
    if (!data?.length) return [];
    // --- UPDATE: oldest → newest when the points carry timestamps
    const ordered = seriesOf(data);
    if (ordered.length >= 2) return ordered.map((p) => p.v);
    // --- END UPDATE
    return data.map(d =>
      typeof d === 'object' ? parseFloat(d.psi ?? d.level ?? d.value ?? 0) : parseFloat(d)
    ).filter(v => !isNaN(v));
  }, [data]);

  if (vals.length < 2) {
    return (
      <svg width="100%" height={height} style={{ display: 'block' }}>
        <line x1="3" y1={height / 2} x2="97%" y2={height / 2}
          stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="3,3" />
      </svg>
    );
  }

  const W   = 200;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const rng = (max - min) || 1;

  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * (W - 6) + 3;
    const y = height - 3 - ((v - min) / rng) * (height - 6);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const last  = pts[pts.length - 1].split(',');
  const gId   = `g${color.replace(/[^a-z0-9]/gi, '')}`;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <polygon points={`${pts.join(' ')} ${W - 3},${height} 3,${height}`}
        fill={`url(#${gId})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.8"
        strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      <circle cx={last[0]} cy={last[1]} r="3" fill={color}
        style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
    </svg>
  );
});

const DataRow = memo(({ label, value, accent, icon: Ic, index = 0, mono = false }) => {
  const rowBg = useColorModeValue('gray.50', 'rgba(255,255,255,0.03)');
  const bd    = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <motion.div custom={index} variants={ROW_SLIDE} initial="hidden" animate="visible">
      <Flex justify="space-between" align="center" bg={rowBg} px={3} py={2}
        borderRadius="lg" borderWidth="1px" borderColor={bd} gap={2}
        _hover={{ borderColor: accent ?? 'blue.300', transform: 'translateX(2px)' }}
        transition="all 0.14s ease">
        <HStack spacing={2} flexShrink={0}>
          {Ic && <Icon as={Ic} color={accent ?? muted} boxSize={3} />}
          <Text fontSize="10px" fontWeight="700" color={muted}
            textTransform="uppercase" letterSpacing="0.05em" fontFamily="sans-serif">
            {label}
          </Text>
        </HStack>
        <Text fontSize="11px" fontWeight="600" color={accent ?? 'inherit'}
          fontFamily={mono ? "mono" : "sans-serif"}
          noOfLines={1} textAlign="right" maxW="56%">
          {value ?? '—'}
        </Text>
      </Flex>
    </motion.div>
  );
});

const SectionLabel = memo(({ label, icon: Ic, accent }) => {
  const muted = useColorModeValue('gray.400', 'gray.500');
  const line  = useColorModeValue('gray.100', 'rgba(255,255,255,0.05)');
  return (
    <HStack spacing={2} mb={1} mt={1}>
      {Ic && <Icon as={Ic} color={accent ?? muted} boxSize={3} />}
      <Text fontSize="10px" fontWeight="700" color={muted} textTransform="uppercase"
        letterSpacing="0.1em" fontFamily="sans-serif">{label}</Text>
      <Box flex={1} h="1px" bg={line} />
    </HStack>
  );
});

const LiveBadge = memo(({ value, unit, color, isStale, isHistoricalMode }) => {
  const activeColor = isHistoricalMode ? '#a78bfa' : color;
  
  return (
    <Flex align="center" gap={2}>
      <Box w="7px" h="7px" borderRadius="full" bg={isStale ? 'gray.500' : activeColor} flexShrink={0}
        style={{
          boxShadow:  isStale ? 'none' : `0 0 8px ${activeColor}`,
          animation:  isStale ? `${staleFlicker} 2s ease-in-out infinite` : `${livePulse} 1.6s infinite`,
        }} />
      <Text fontSize="22px" fontWeight="700" lineHeight="1"
        color={isStale ? 'gray.400' : activeColor}
        fontFamily="mono"
        style={{ textShadow: isStale ? 'none' : `0 0 14px ${activeColor}55` }}>
        {value ?? '—'}
        {unit && <Text as="span" fontSize="11px" fontWeight="500" color="gray.400" ml={1} fontFamily="sans-serif">{unit}</Text>}
      </Text>
    </Flex>
  );
});

const TimestampChip = memo(({ lastUpdated, isStale, isHistoricalMode }) => {
  const [display, setDisplay] = useState('—');
  const muted = useColorModeValue('gray.400', 'gray.500');

  useEffect(() => {
    if (!lastUpdated) return;

    if (isHistoricalMode) {
      const date = new Date(lastUpdated);
      setDisplay(date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      return;
    }

    const tick = () => {
      const s = Math.floor((Date.now() - lastUpdated) / 1000);
      setDisplay(
        s < 5    ? 'just now' :
        s < 60   ? `${s}s ago` :
        s < 3600 ? `${Math.floor(s / 60)}m ago` :
                   `${Math.floor(s / 3600)}h ago`
      );
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [lastUpdated, isHistoricalMode]);

  return (
    <HStack spacing={1}>
      <Icon as={isHistoricalMode ? FaHistory : FaClock} color={isStale ? 'red.400' : muted} boxSize={2.5} />
      <Text fontSize="10px" color={isStale ? 'red.400' : muted} fontFamily="sans-serif" fontWeight="600">
        {isHistoricalMode ? `HISTORICAL · ${display}` : (isStale ? `STALE · ${display}` : display)}
      </Text>
    </HStack>
  );
});

const SeverityBadge = memo(({ severity, label, color }) => {
  const bg    = { ok: 'rgba(52,211,153,0.08)', warning: 'rgba(245,158,11,0.08)', critical: 'rgba(239,68,68,0.1)', info: 'rgba(34,211,238,0.08)', none: 'transparent' };
  const icons = { ok: FaCheckCircle, warning: FaExclamationTriangle, critical: FaTimesCircle, info: MdInfo, none: FaMinus };
  const Ic    = icons[severity] ?? FaMinus;
  return (
    <Flex align="center" gap={1.5} px={2} py={1} borderRadius="md"
      bg={bg[severity] ?? 'transparent'} borderWidth="1px" borderColor={`${color}44`}>
      <Icon as={Ic} color={color} boxSize={2.5} />
      <Text fontSize="10px" fontWeight="600" color={color} fontFamily="sans-serif" letterSpacing="0.05em">{label}</Text>
    </Flex>
  );
});

const StatusChip = memo(({ status }) => {
  const s = status?.toLowerCase() ?? '';
  const cfg = ['operational','active'].includes(s)
    ? { c: 'green',  ic: FaCheckCircle,     lbl: 'OPERATIONAL' }
    : ['damaged','critical','offline'].includes(s)
    ? { c: 'red',    ic: FaTimesCircle,     lbl: s.toUpperCase() }
    : ['maintenance_required','maintenance'].includes(s)
    ? { c: 'orange', ic: FaWrench,          lbl: 'MAINTENANCE'  }
    : { c: 'gray',   ic: FaTimesCircle,     lbl: s.toUpperCase() || 'UNKNOWN' };

  const bg  = useColorModeValue(`${cfg.c}.50`,  `${cfg.c}.900`);
  const bd  = useColorModeValue(`${cfg.c}.300`, `${cfg.c}.600`);
  const txt = useColorModeValue(`${cfg.c}.800`, `${cfg.c}.200`);
  const ic  = useColorModeValue(`${cfg.c}.600`, `${cfg.c}.300`);

  return (
    <HStack spacing={2} px={3} py={1.5} bg={bg} borderRadius="md"
      borderWidth="1px" borderColor={bd} boxShadow="sm">
      <Icon as={cfg.ic} color={ic} boxSize={3.5} />
      <Text fontSize="10px" fontWeight="700" color={txt} letterSpacing="wider" fontFamily="sans-serif">
        {cfg.lbl}
      </Text>
    </HStack>
  );
});

const Expandable = memo(({ title, icon: Ic, count, children, defaultOpen = false, accentColor }) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });
  const cardBg  = useColorModeValue('gray.50',  'rgba(255,255,255,0.02)');
  const hoverBg = useColorModeValue('gray.100', 'rgba(255,255,255,0.05)');
  const bd      = useColorModeValue('gray.200', 'rgba(255,255,255,0.07)');
  const muted   = useColorModeValue('gray.500', 'gray.400');
  const iconCol = useColorModeValue('blue.600', 'blue.300');

  return (
    <MotionBox variants={FADE_UP}>
      <Box borderWidth="1px" borderColor={bd} borderRadius="xl" overflow="hidden" bg={cardBg}>
        <Flex as="button" w="100%" p={3} onClick={onToggle} align="center"
          justify="space-between" _hover={{ bg: hoverBg }} transition="all 0.2s" cursor="pointer">
          <HStack spacing={2}>
            {Ic && <Icon as={Ic} boxSize={3.5} color={iconCol} />}
            <Text fontWeight="600" fontSize="sm" fontFamily="sans-serif">{title}</Text>
            {count > 0 && (
              <Badge colorScheme={accentColor ? undefined : 'blue'} borderRadius="full"
                px={2} fontSize="xs" fontWeight="700">{count}</Badge>
            )}
          </HStack>
          <Icon as={isOpen ? MdExpandLess : MdExpandMore} boxSize={5} color={muted} />
        </Flex>
        <Collapse in={isOpen} animateOpacity>
          <Box p={3} pt={0}>{children}</Box>
        </Collapse>
      </Box>
    </MotionBox>
  );
});

const SensorRow = memo(({ sensor, sensorReadings, psiConditions, isHistoricalMode, historicalTimestamp, onOpenSensor = null, intelRow = null, historyOf = null }) => { // --- UPDATE: + onOpenSensor, intelRow, historyOf
  const deviceId = sensor.device_id ?? sensor.id;
  const type     = sensor.type ?? 'pressure';
  const meta     = sensorMeta(type);
  const external = sensorReadings?.[deviceId];

  const { liveReading, history, lastUpdated, isStale } = useTelemetryFeed(deviceId, type, external, isHistoricalMode, historicalTimestamp);
  const cond = useConditionEngine(liveReading, psiConditions);
  const { isOpen, onToggle } = useDisclosure();

  const bd     = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');
  const cardBg = useColorModeValue('gray.50',  'rgba(255,255,255,0.025)');
  const muted  = useColorModeValue('gray.500', 'gray.400');
  const rowHov = useColorModeValue('white',    'rgba(255,255,255,0.04)');

  const historyBg = useColorModeValue('white', 'gray.900');
  // --- UPDATE: page's live buffer when it is longer than this row's own
  const pageHistory = historyOf && !isHistoricalMode ? historyOf(deviceId) : null;
  const chartHistory = pageHistory && pageHistory.length > history.length ? pageHistory : history;
  // --- END UPDATE

  const displayVal = typeof liveReading === 'number'
    ? liveReading.toFixed(2)
    : typeof liveReading === 'string' ? liveReading.toUpperCase() : null;

  return (
    <Box borderWidth="1px" borderColor={`${meta.color}33`} borderRadius="xl"
      overflow="hidden" _hover={{ borderColor: meta.color }} transition="border-color 0.2s">

      <Box position="relative" px={4} py={3} bg={cardBg}
        cursor="pointer" onClick={onToggle}
        _hover={{ bg: rowHov }} transition="background 0.15s">

        <Box position="absolute" left={0} top={0} bottom={0} w="2.5px"
          bg={meta.color} style={{ boxShadow: `0 0 8px ${meta.color}` }} />

        <Flex justify="space-between" align="flex-start" mb={2}>
          <HStack spacing={2}>
            <Icon as={meta.icon} color={meta.color} boxSize={3.5} />
            <VStack spacing={0} align="flex-start">
              <Text fontSize="10px" fontWeight="700" color={meta.color} fontFamily="mono" letterSpacing="0.05em">{deviceId}</Text>
              <Text fontSize="10px" color={muted} fontFamily="sans-serif">{meta.label}</Text>
            </VStack>
          </HStack>
          <VStack align="flex-end" spacing={1}>
            <LiveBadge value={displayVal} unit={meta.unit} color={meta.color} isStale={isStale} isHistoricalMode={isHistoricalMode} />
            <HStack spacing={2}>
              <TimestampChip lastUpdated={lastUpdated} isStale={isStale} isHistoricalMode={isHistoricalMode} />
              {cond.severity !== 'none' && (
                <SeverityBadge severity={cond.severity} label={cond.label} color={cond.color} />
              )}
            </HStack>
          </VStack>
        </Flex>

        {chartHistory.length >= 2 && ( /* --- UPDATE: chartHistory */
          <Box mt={1} mb={1}>
            <MiniSparkline data={chartHistory} color={meta.color} height={26} />
          </Box>
        )}

        {/* --- UPDATE: ML flag + open the sensor drawer */}
        {(intelRow?.anomaly || intelRow?.stale || intelRow?.flat || onOpenSensor) && (
          <Flex justify="space-between" align="center" mt={1} gap={2}>
            <HStack spacing={1}>
              {intelRow?.anomaly && <Badge colorScheme={intelRow.anomaly.level === 'critical' ? 'red' : 'orange'} fontSize="9px">ML: {intelRow.anomaly.kind === 'drop' ? 'sudden drop' : 'spike'}</Badge>}
              {intelRow?.stale && <Badge colorScheme="orange" variant="outline" fontSize="9px">STALE</Badge>}
              {intelRow?.flat && <Badge colorScheme="yellow" fontSize="9px">possibly stuck</Badge>}
            </HStack>
            {onOpenSensor && <Button size="xs" variant="ghost" colorScheme="blue" fontSize="10px" onClick={(e) => { e.stopPropagation(); onOpenSensor(sensor); }}>Sensor intelligence</Button>}
          </Flex>
        )}
        {/* --- END UPDATE */}

        <Flex justify="flex-end">
          <Text fontSize="10px" color={muted} fontFamily="sans-serif"
            _hover={{ color: meta.color }} transition="color 0.15s">
            {isOpen ? '▲ HIDE' : `▼ HISTORY (${history.length})`}
          </Text>
        </Flex>
      </Box>

      <Collapse in={isOpen} animateOpacity>
        <Box maxH="120px" overflowY="auto" bg={historyBg} css={{ scrollbarWidth: 'thin' }}>
          {history.slice(0, 14).map((r, i) => (
            <Flex key={i} justify="space-between" px={4} py={1.5}
              borderTopWidth="1px" borderColor={bd}
              fontSize="11px" fontFamily="sans-serif"
              _hover={{ bg: cardBg }}>
              <Text color={meta.color} fontWeight="600" fontFamily="mono">
                {(r.psi ?? r.level ?? r.value ?? '—').toString()}
                {r.psi != null && <Text as="span" color={muted} ml={1} fontFamily="sans-serif">PSI</Text>}
              </Text>
              <Text color={muted} fontFamily="mono">
                {r.timestamp ? new Date(r.timestamp).toLocaleTimeString() : '—'}
              </Text>
            </Flex>
          ))}
          {history.length === 0 && (
            <Text px={4} py={3} fontSize="11px" color={muted} fontFamily="sans-serif">No history available</Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

const MaintenanceForm = memo(({ assetId, dispatch, isActing, onClose }) => {
  const [form, setForm] = useState({
    type:       'preventive',
    priority:   'medium',
    dueDate:    '',
    assignedTo: '',
    notes:      '',
  });
  const [errors, setErrors] = useState({});

  const cardBg = useColorModeValue('gray.50',  'rgba(255,255,255,0.025)');
  const muted  = useColorModeValue('gray.500', 'gray.400');

  const validate = () => {
    const e = {};
    if (!form.dueDate)    e.dueDate    = 'Due date is required';
    if (!form.assignedTo) e.assignedTo = 'Assignee is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    dispatch('SCHEDULE_MAINTENANCE', { ...form, assetId });
    onClose();
  };

  const field = (key, label, el) => (
    <FormControl isInvalid={!!errors[key]}>
      <FormLabel fontSize="10px" color={muted} fontFamily="sans-serif" textTransform="uppercase" letterSpacing="0.05em" mb={1}>{label}</FormLabel>
      {el}
      {errors[key] && <Text fontSize="10px" color="red.400" mt={1}>{errors[key]}</Text>}
    </FormControl>
  );

  const inputProps = {
    size: 'sm', borderRadius: 'lg',
    fontFamily: "sans-serif", fontSize: '12px',
    bg: useColorModeValue('white', 'gray.700'),
  };

  return (
    <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor="rgba(167,139,250,0.3)" p={4}>
      <HStack spacing={2} mb={3}>
        <Icon as={FaCalendarAlt} color="#a78bfa" boxSize={3.5} />
        <Text fontSize="11px" fontWeight="700" color="#a78bfa" fontFamily="sans-serif" letterSpacing="0.1em">
          SCHEDULE MAINTENANCE
        </Text>
      </HStack>

      <VStack spacing={3} align="stretch">
        {field('type', 'Type',
          <Select {...inputProps} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
            <option value="preventive">Preventive</option>
            <option value="corrective">Corrective</option>
            <option value="predictive">Predictive</option>
            <option value="emergency">Emergency</option>
          </Select>
        )}
        {field('priority', 'Priority',
          <Select {...inputProps} value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </Select>
        )}
        {field('dueDate', 'Due Date',
          <Input {...inputProps} type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
        )}
        {field('assignedTo', 'Assigned To',
          <Input {...inputProps} placeholder="Technician name or team" value={form.assignedTo} onChange={e => setForm(p => ({ ...p, assignedTo: e.target.value }))} />
        )}
        {field('notes', 'Notes (optional)',
          <Textarea {...inputProps} placeholder="Task description, tools needed, safety notes..." value={form.notes} rows={3} resize="none" onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        )}
        <HStack pt={1}>
          <Button size="sm" colorScheme="purple" flex={1} isLoading={isActing} onClick={handleSubmit} fontFamily="sans-serif" fontSize="11px" letterSpacing="0.05em">
            CONFIRM SCHEDULE
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} fontFamily="sans-serif" fontSize="11px">
            CANCEL
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
});

const HealthTrendBar = memo(({ history, trend }) => {
  const muted  = useColorModeValue('gray.400', 'gray.500');
  const cardBg = useColorModeValue('gray.50',  'rgba(255,255,255,0.025)');
  const bd     = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');

  const trendConfig = {
    degrading:  { icon: MdTrendingDown, color: '#ef4444', label: 'DEGRADING'  },
    recovering: { icon: MdTrendingUp,   color: '#34d399', label: 'RECOVERING' },
    stable:     { icon: MdTrendingFlat, color: '#f59e0b', label: 'STABLE'     },
  };
  const cfg = trendConfig[trend] ?? trendConfig.stable;

  if (history.length < 3) return null;

  const scores = history.slice(-20).map(h => h.score);
  const latest = scores[scores.length - 1];

  return (
    <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={bd} px={3} py={3}>
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <Icon as={cfg.icon} color={cfg.color} boxSize={3.5} />
          <Text fontSize="10px" fontWeight="700" color={cfg.color} fontFamily="sans-serif" letterSpacing="0.05em">
            TREND: {cfg.label}
          </Text>
        </HStack>
        <Text fontSize="16px" fontWeight="700" color={cfg.color} fontFamily="mono">{latest?.toFixed(1)}%</Text>
      </Flex>
      <MiniSparkline data={scores} color={cfg.color} height={24} />
      <Text fontSize="9px" color={muted} mt={1} fontFamily="sans-serif" letterSpacing="0.05em">
        LAST {history.length} SNAPSHOTS
      </Text>
    </Box>
  );
});

const EmptyState = memo(() => {
  const bd    = useColorModeValue('gray.200', 'rgba(255,255,255,0.07)');
  const muted = useColorModeValue('gray.400', 'gray.500');
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
      <Flex align="center" justify="center" minH="260px" borderRadius="xl" borderWidth="2px" borderStyle="dashed" borderColor={bd} p={6}>
        <VStack spacing={3}>
          <Text fontSize="34px" color={muted} opacity={0.25} style={{ animation: `${floatAnim} 3.2s ease-in-out infinite` }}>⬡</Text>
          <Text color={muted} fontSize="12px" fontWeight="600" letterSpacing="0.1em" fontFamily="sans-serif" textTransform="uppercase">
            Select an asset or pipeline
          </Text>
          <Text color={muted} fontSize="11px" textAlign="center" opacity={0.8} maxW="200px" lineHeight="1.6" fontFamily="sans-serif">
            Click any asset or pipeline in the scene to view live telemetry, linked sensors, and AI diagnostics
          </Text>
        </VStack>
      </Flex>
    </motion.div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const AssetInformationPanel = ({
  asset,
  assetHealth,
  onViewFullAsset,
  sensorReadings      = {},
  psiConditions       = [],
  sensorPipelineLinks = {},
  issues              = [],
  alerts              = [],
  openWorkOrders      = [],
  predictedFailures   = [],
  isLoading           = false,
  onAction,
  autoRefreshMs       = 0,
  isHistoricalMode    = false,
  historicalTimestamp = null,
  // --- UPDATE
  sensors             = null,
  onOpenTwin          = null,
  onOpenSensor        = null,
  onRefresh           = null,
  riskScore           = null,
  intel               = null,
  liveStatus          = null,
  // --- END UPDATE
}) => {
  const [showScheduler, setShowScheduler] = useState(false);
  const [activeTab,     setActiveTab]     = useState(0);
  const [isRefreshing,  setIsRefreshing]  = useState(false);
  const toast = useToast();

  const bg          = useColorModeValue('white',    'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText   = useColorModeValue('gray.500', 'gray.400');
  const cardBg      = useColorModeValue('gray.50',  'rgba(255,255,255,0.025)');

  // FIX: Unconditional hook calls at the top level
  const histHeaderGrad = useColorModeValue('linear(to-r, purple.500, pink.400)', 'linear(to-r, #a78bfa, #f472b6)');
  const liveHeaderGrad = useColorModeValue('linear(to-r, blue.600, teal.500)', 'linear(to-r, #22d3ee, #34d399)');
  const headerGrad     = isHistoricalMode ? histHeaderGrad : liveHeaderGrad;

  const alertBg     = useColorModeValue('red.50',    'rgba(239,68,68,0.07)');
  const alertBd     = useColorModeValue('red.200',   'rgba(239,68,68,0.22)');
  const alertTxt    = useColorModeValue('red.800',   '#ef4444');
  const issueBg     = useColorModeValue('orange.50', 'rgba(245,158,11,0.07)');
  const issueBd     = useColorModeValue('orange.200','rgba(245,158,11,0.22)');
  const issueTxt    = useColorModeValue('orange.800','#f59e0b');
  const woBdHov     = useColorModeValue('blue.300',  'blue.500');
  const tabBg       = useColorModeValue('gray.100',  'rgba(255,255,255,0.04)');
  const tabActiveB  = useColorModeValue('white',     'rgba(255,255,255,0.09)');
  const skeletonSt  = useColorModeValue('gray.100',  'gray.700');
  
  const headerBg    = useColorModeValue(
    'linear-gradient(135deg,rgba(66,153,225,0.04),rgba(56,178,172,0.04))',
    'linear-gradient(135deg,rgba(66,153,225,0.06),rgba(56,178,172,0.06))'
  );

  const statBoxBg   = useColorModeValue('white', 'rgba(255,255,255,0.03)');
  const twinPromoBg = useColorModeValue('purple.50', 'rgba(167,139,250,0.05)');
  const progressBg  = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');

  const refreshSignal = useAutoRefresh(autoRefreshMs);
  // --- UPDATE: manual refresh feeds the loader too
  const [manualRefresh, setManualRefresh] = useState(0);
  const aiCardBg = useColorModeValue('purple.50', 'rgba(167,139,250,0.06)');
  // --- END UPDATE
  
  const { enriched, linkedSensors, isLoading: isEnriching, error: enrichError }
    = useFlowSenseAsset(asset, refreshSignal + manualRefresh, sensorPipelineLinks, sensors); // --- UPDATE: + manualRefresh, sensors

  const handleActionComplete = useCallback((result) => {
    onAction?.(result);
    toast({
      title:      result.success ? 'Action Complete' : 'Action Failed',
      status:     result.success ? 'success' : 'error',
      duration:   3000,
      position:   'top-right',
      isClosable: true,
    });
  }, [onAction, toast]);

  const { dispatch: actionDispatch, isActing, lastAction }
    = useActionDispatcher(asset?.id, handleActionComplete);

  const { hasCopied, onCopy } = useClipboard(asset?.id || asset?.device_id || '');

  const primarySensorObj = linkedSensors.find(s => (asset?.sensor_id != null) && (String(s.device_id) === String(asset?.sensor_id) || String(s.id) === String(asset?.sensor_id))) || linkedSensors[0]; // --- UPDATE: string-safe
  const primarySensorId  = primarySensorObj?.device_id || primarySensorObj?.id || asset?.sensor_id;

  const primaryReading = sensorReadings?.[primarySensorId];
  
  const {
    liveReading: primaryLive, history: primaryHistory,
    lastUpdated: primaryTs,   isStale:   primaryStale,
  } = useTelemetryFeed(
    primarySensorId,
    primarySensorObj?.type ?? asset?.sensor_type ?? 'pressure',
    primaryReading,
    isHistoricalMode,
    historicalTimestamp
  );
  
  const primaryCond = useConditionEngine(primaryLive, psiConditions);
  const { history: healthHistory, trend: healthTrend } = useAssetHealthHistory(assetHealth);

  // --- UPDATE: string-safe id matching (ids arrive as numbers and strings)
  const sameId = (x) => x !== undefined && x !== null && asset?.id !== undefined && String(x) === String(asset.id);
  const relatedPrediction = useMemo(() => predictedFailures.find(p => sameId(p.asset_id)), [predictedFailures, asset?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const relatedWorkOrders = useMemo(() => openWorkOrders.filter(w => sameId(w.asset_id)), [openWorkOrders, asset?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const relatedAlerts = useMemo(() => alerts.filter(a => sameId(a.asset_id) || (a.sensor_id != null && linkedSensors.some((s) => String(s.device_id ?? s.id) === String(a.sensor_id) || String(s.id) === String(a.sensor_id)))), [alerts, asset?.id, linkedSensors]); // eslint-disable-line react-hooks/exhaustive-deps
  const relatedIssues = useMemo(() => issues.filter(i => sameId(i.asset_id)), [issues, asset?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- END UPDATE

  const currentReading = primaryLive ?? sensorReadings?.[primarySensorId];
  let displayReading = currentReading;
  let metricLabel    = 'Reading';
  
  if (typeof currentReading === 'string') {
    metricLabel    = 'Water Level';
    displayReading = currentReading.toUpperCase();
  } else if (typeof currentReading === 'number') {
    metricLabel    = 'Pressure';
    displayReading = `${currentReading.toFixed(2)} PSI`;
    // --- UPDATE: numeric water levels are not PSI
    if ((primarySensorObj?.type ?? asset?.sensor_type) === 'water_level') { metricLabel = 'Water Level'; displayReading = currentReading.toFixed(2); }
    // --- END UPDATE
  }

  const typeLabel = asset?.isSensor ? 'SENSOR' : asset?.isPipeline ? 'PIPELINE' : (asset?.type ?? 'ASSET');

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    // --- UPDATE: really refresh (was an 800 ms wait + success toast)
    try {
      setManualRefresh((n) => n + 1);
      if (typeof onRefresh === 'function') await onRefresh();
      toast({ title: 'Telemetry Refreshed', status: 'success', duration: 2000, position: 'top-right', isClosable: true });
    } catch (e) {
      toast({ title: 'Refresh failed', description: e?.message, status: 'error', duration: 4000, position: 'top-right', isClosable: true });
    } finally {
      setIsRefreshing(false);
    }
    // --- END UPDATE
  }, [toast, onRefresh]);

  const handleExport = useCallback(() => {
    const payload = {
      asset_id:       asset?.id,
      asset_name:     asset?.name,
      asset_type:     asset?.type,
      status:         asset?.status,
      sensor_reading: currentReading,
      health_score:   assetHealth?.health_score,
      health_status:  assetHealth?.health_status,
      predicted_rul:  relatedPrediction?.predicted_rul_days,
      linked_sensors: linkedSensors.length,
      work_orders:    relatedWorkOrders.length,
      active_alerts:  relatedAlerts.length,
      open_issues:    relatedIssues.length,
      health_trend:   healthTrend,
      exported_at:    new Date().toISOString(),
      historical_mode: isHistoricalMode,
      historical_time: historicalTimestamp
    };
    const uri = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(payload, null, 2));
    const a = document.createElement('a');
    a.href = uri; a.download = `asset-${asset?.id}-${Date.now()}.json`; a.click();
    toast({ title: 'Summary Exported', status: 'success', duration: 2000, position: 'top-right', isClosable: true });
  }, [asset, currentReading, assetHealth, relatedPrediction, linkedSensors, relatedWorkOrders, relatedAlerts, relatedIssues, healthTrend, isHistoricalMode, historicalTimestamp, toast]);

  const activeSensorCount = linkedSensors.filter(s => sensorReadings?.[s.device_id ?? s.id] != null).length;
  const totalActivity     = relatedWorkOrders.length + relatedAlerts.length + relatedIssues.length;

  if (!asset && !isLoading) return <EmptyState />;

  if (isLoading) {
    return (
      <Box w="100%" bg={bg} p={4} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
        <VStack spacing={4} align="stretch">
          {[64, 40, 100, 80].map((h, i) => (
            <Skeleton key={i} height={`${h}px`} borderRadius="lg" startColor={skeletonSt} />
          ))}
        </VStack>
      </Box>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <MotionBox
        key={asset.id || asset.device_id}
        variants={STAGGER}
        initial="hidden"
        animate="visible"
        exit="exit"
        w="100%"
        bg={bg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="lg"
        overflow="hidden"
      >
        <Box px={4} pt={4} pb={3} borderBottomWidth="1px" borderColor={borderColor} background={headerBg}>
          <MotionBox variants={FADE_UP}>
            <Flex justify="space-between" align="flex-start" mb={2}>
              <Box flex={1} overflow="hidden" pr={2}>
                <Heading size="md" noOfLines={2} title={asset.name || asset.id}
                  bgGradient={headerGrad} bgClip="text" fontWeight="700"
                  lineHeight="1.2" fontFamily="sans-serif" mb={1}>
                  {asset.name || (asset.isPipeline ? `Pipeline ${asset.id}` : asset.device_id || 'Unknown Asset')}
                </Heading>
                <HStack spacing={2} flexWrap="wrap">
                  <HStack spacing={1}>
                    <Text fontSize="11px" color={mutedText} fontFamily="mono">
                      ID: {asset.id || asset.device_id}
                    </Text>
                    <Tooltip label={hasCopied ? 'Copied!' : 'Copy ID'} placement="top" hasArrow>
                      <IconButton
                        icon={hasCopied ? <MdCheck /> : <MdContentCopy />}
                        size="xs" variant="ghost" minW="auto" h="auto"
                        color={hasCopied ? 'green.500' : mutedText}
                        onClick={onCopy} aria-label="Copy asset ID"
                        _hover={{ color: 'blue.500' }} />
                    </Tooltip>
                  </HStack>
                  <Tag size="sm" borderRadius="full" colorScheme="blue" variant="subtle" fontFamily="sans-serif" fontSize="10px" fontWeight="600">
                    {typeLabel.toUpperCase()}
                  </Tag>
                  {isHistoricalMode && (
                    <Tag size="sm" borderRadius="full" colorScheme="purple" variant="solid" fontFamily="sans-serif" fontSize="10px" fontWeight="600">
                      <Icon as={FaHistory} mr={1} /> HISTORICAL
                    </Tag>
                  )}
                  {autoRefreshMs > 0 && !isHistoricalMode && (
                    <Tag size="sm" borderRadius="full" colorScheme="green" variant="subtle" fontFamily="sans-serif" fontSize="10px" fontWeight="600">
                      AUTO {autoRefreshMs / 1000}s
                    </Tag>
                  )}
                  {enrichError && (
                    <Tooltip label={`Enrichment: ${enrichError}`} hasArrow>
                      <Icon as={FaExclamationCircle} color="orange.400" boxSize={3} />
                    </Tooltip>
                  )}
                </HStack>
              </Box>

              <Menu placement="bottom-end">
                <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" color={mutedText} aria-label="Asset actions" flexShrink={0} _hover={{ color: 'blue.500', bg: cardBg }} />
                <Portal>
                  <MenuList fontSize="sm" boxShadow="xl" fontFamily="sans-serif">
                    <MenuItem fontSize="12px" icon={isRefreshing ? <Spinner size="xs" /> : <MdRefresh />} onClick={handleRefresh} isDisabled={isRefreshing || isHistoricalMode}>
                      Refresh Telemetry
                    </MenuItem>
                    <MenuItem fontSize="12px" icon={<FaFileExport />} onClick={handleExport}>
                      Export JSON
                    </MenuItem>
                    <MenuItem fontSize="12px" icon={<FaChartLine />} onClick={() => onViewFullAsset?.(enriched ?? asset)}>
                      Full Analytics
                    </MenuItem>
                    {!asset.isSensor && !asset.isPipeline && (
                      <MenuItem fontSize="12px" icon={<FaCalendarAlt />} onClick={() => setShowScheduler(v => !v)}>
                        Schedule Maintenance
                      </MenuItem>
                    )}
                    <MenuItem fontSize="12px" icon={<FaRobot />} onClick={() => (onOpenTwin ? onOpenTwin(enriched ?? asset) : onViewFullAsset?.(enriched ?? asset))}>{/* --- UPDATE: twin drawer */}
                      Open Digital Twin
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </Flex>

            <Flex gap={2} align="center" flexWrap="wrap" mt={2}>
              <StatusChip status={asset.status || 'active'} />
              {linkedSensors.length > 0 ? (
                <Badge colorScheme={activeSensorCount > 0 ? 'green' : 'gray'} variant="subtle" px={2} py={1} borderRadius="md" fontFamily="sans-serif" fontSize="11px">
                  {activeSensorCount}/{linkedSensors.length} SENSORS
                </Badge>
              ) : (
                <Badge colorScheme="gray" variant="solid" px={2} py={1} borderRadius="md" fontSize="11px" fontFamily="sans-serif">NO SENSORS</Badge>
              )}
              {totalActivity > 0 && (
                <Badge colorScheme="orange" variant="subtle" px={2} py={1} borderRadius="md" fontFamily="sans-serif" fontSize="11px">
                  {totalActivity} OPEN TASKS
                </Badge>
              )}
            </Flex>
          </MotionBox>
        </Box>

        {(relatedPrediction || assetHealth?.health_status === 'critical') && (
          <MotionBox variants={FADE_UP} px={4} pt={3}>
            <Alert status="error" borderRadius="lg" bg={alertBg} borderWidth="1px" borderColor={alertBd} py={2} px={3} alignItems="flex-start">
              <AlertIcon as={FaBrain} boxSize={4} color={alertTxt} mt={0.5} />
              <Box flex={1}>
                <AlertTitle fontSize="11px" fontWeight="700" color={alertTxt} fontFamily="sans-serif" letterSpacing="0.05em">
                  AI FAILURE PREDICTION
                </AlertTitle>
                <AlertDescription fontSize="xs" color={alertTxt} fontFamily="sans-serif">
                  Est. remaining useful life:{' '}
                  <Text as="span" fontWeight="700" fontFamily="mono">
                    {(assetHealth?.predicted_rul_days ?? relatedPrediction?.predicted_rul_days) ?? '—'} days
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>
          </MotionBox>
        )}

        <Collapse in={showScheduler} animateOpacity>
          <Box px={4} pt={3}>
            <MaintenanceForm assetId={asset.id} dispatch={actionDispatch} isActing={isActing} onClose={() => setShowScheduler(false)} />
          </Box>
        </Collapse>

        <Box px={4} pt={4} pb={4}>
          <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled" isLazy>
            <TabList bg={tabBg} borderRadius="xl" p={1} mb={4} display="grid" gridTemplateColumns="repeat(4,1fr)" gap={1}>
              {[
                { label: 'OVERVIEW', icon: MdDashboard       },
                { label: 'SENSORS',  icon: MdSensors,  badge: activeSensorCount > 0 ? activeSensorCount : null },
                { label: 'HEALTH',   icon: MdHealthAndSafety },
                { label: 'ACTIVITY', icon: MdEventNote, badge: totalActivity > 0 ? totalActivity : null },
              ].map((t, i) => (
                <Tab key={t.label} bg={activeTab === i ? tabActiveB : 'transparent'} borderRadius="lg" py={1.5} px={1} fontFamily="sans-serif" fontSize="9px" fontWeight="700" letterSpacing="0.05em" color={activeTab === i ? 'blue.500' : mutedText} transition="all 0.2s" _hover={{ color: 'blue.400' }} boxShadow={activeTab === i ? 'sm' : 'none'} position="relative">
                  <VStack spacing={0.5}>
                    <Icon as={t.icon} boxSize={3.5} />
                    <Text>{t.label}</Text>
                  </VStack>
                  {t.badge && (
                    <Box position="absolute" top="-2px" right="-2px" w="14px" h="14px" borderRadius="full" bg="red.500" display="flex" alignItems="center" justifyContent="center">
                      <Text fontSize="8px" color="white" fontWeight="700" fontFamily="sans-serif">{t.badge}</Text>
                    </Box>
                  )}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {/* ────────────────── TAB: OVERVIEW ────────────────── */}
              <TabPanel px={0} pb={0}>
                <MotionBox variants={STAGGER} initial="hidden" animate="visible">
                  <VStack spacing={3} align="stretch">
                    {primarySensorId && (
                      <MotionBox variants={FADE_UP}>
                        <Box bg={cardBg} borderRadius="xl" px={4} py={3} borderWidth="1px" borderColor={isHistoricalMode ? 'purple.500' : `${primaryCond.color}33`} position="relative" overflow="hidden">
                          <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={isHistoricalMode ? '#a78bfa' : primaryCond.color} style={{ boxShadow: `0 0 8px ${isHistoricalMode ? '#a78bfa' : primaryCond.color}` }} />
                          <Flex justify="space-between" align="flex-start" mb={2}>
                            <VStack align="flex-start" spacing={1}>
                              <Text fontSize="10px" fontWeight="700" color={mutedText} fontFamily="sans-serif" letterSpacing="0.05em">
                                PRIMARY SENSOR · <Text as="span" fontFamily="mono">{primarySensorId}</Text>
                              </Text>
                              <LiveBadge
                                value={typeof primaryLive === 'number' ? primaryLive.toFixed(2) : typeof primaryLive === 'string' ? primaryLive.toUpperCase() : null}
                                unit={metricLabel === 'Pressure' ? 'PSI' : ''} color={primaryCond.color} isStale={primaryStale} isHistoricalMode={isHistoricalMode}
                              />
                            </VStack>
                            <VStack align="flex-end" spacing={1}>
                              <TimestampChip lastUpdated={primaryTs} isStale={primaryStale} isHistoricalMode={isHistoricalMode} />
                              <SeverityBadge severity={primaryCond.severity} label={primaryCond.label} color={primaryCond.color} />
                            </VStack>
                          </Flex>
                          {primaryHistory.length >= 2 && (
                            <MiniSparkline data={primaryHistory} color={isHistoricalMode ? '#a78bfa' : primaryCond.color} height={30} />
                          )}
                        </Box>
                      </MotionBox>
                    )}

                    {/* --- UPDATE: FlowSense AI card */}
                    {(riskScore || intel) && !asset.isSensor && (
                      <MotionBox variants={FADE_UP}>
                        <Box bg={aiCardBg} borderRadius="xl" px={3} py={3} borderWidth="1px" borderColor="rgba(167,139,250,0.3)" role="region" aria-label="FlowSense AI for this asset">
                          <Flex justify="space-between" align="center" mb={1}>
                            <HStack spacing={2}><Icon as={FaBrain} color="#a78bfa" boxSize={3.5} /><Text fontSize="10px" fontWeight="700" color="#a78bfa" fontFamily="sans-serif" letterSpacing="0.05em">FLOWSENSE AI</Text></HStack>
                            {riskScore?.score !== null && riskScore?.score !== undefined ? <Badge colorScheme={riskScore.color} borderRadius="full">RISK {riskScore.score}/100 · {riskScore.label}</Badge> : <Badge colorScheme="gray" borderRadius="full">NOT SCORED</Badge>}
                          </Flex>
                          <Text fontSize="11px" whiteSpace="pre-wrap" fontFamily="sans-serif" lineHeight="1.6">{explainAsset(riskScore)}</Text>
                        </Box>
                      </MotionBox>
                    )}
                    {/* --- END UPDATE */}
                    <SectionLabel label={`${typeLabel} Details`} icon={FaLayerGroup} />
                    <VStack spacing={1.5} align="stretch">
                      {[
                        { label: 'Type',      value: (enriched?.type ?? asset.type ?? asset.asset_type)?.toUpperCase() },
                        { label: 'Material',  value: enriched?.material ?? asset.material, mono: true },
                        { label: 'Diameter',  value: (enriched?.diameter ?? asset.diameter) ? `${enriched?.diameter ?? asset.diameter} in` : null, mono: true },
                        { label: 'Length',    value: (enriched?.length ?? asset.length) ? `${parseFloat(enriched?.length ?? asset.length).toFixed(1)} m` : null, mono: true },
                        { label: 'Zone',      value: enriched?.zone ?? asset.zone ?? asset.district },
                        { label: 'Operator',  value: enriched?.operator ?? asset.operator },
                        { label: 'Installed', value: enriched?.installed_year ?? asset.installed_year, mono: true },
                        { label: 'Capacity',  value: (enriched?.capacity ?? asset.capacity) ? `${enriched?.capacity ?? asset.capacity} ${enriched?.capacity_unit ?? asset.capacity_unit ?? 'gal'}` : null, mono: true },
                        { label: 'Depth',     value: (enriched?.depth_m ?? asset.depth_m) ? `${enriched?.depth_m ?? asset.depth_m}m` : null,  mono: true },
                        { label: 'Lat',       value: (enriched?.latitude ?? asset.latitude) ? parseFloat(enriched?.latitude ?? asset.latitude).toFixed(6) : null, icon: FaMapMarkerAlt, mono: true },
                        { label: 'Lon',       value: (enriched?.longitude ?? asset.longitude) ? parseFloat(enriched?.longitude ?? asset.longitude).toFixed(6) : null, icon: FaMapMarkerAlt, mono: true },
                      ].filter(r => r.value != null).map((r, i) => (
                        <DataRow key={r.label} label={r.label} value={r.value} icon={r.icon} mono={r.mono} index={i} />
                      ))}
                    </VStack>

                    <SimpleGrid columns={3} spacing={2}>
                      {[
                        { label: 'SENSORS',    value: linkedSensors.length,       color: '#22d3ee' },
                        { label: 'WORK ORDERS',value: relatedWorkOrders.length,   color: relatedWorkOrders.length ? '#f59e0b' : '#34d399' },
                        { label: 'ALERTS',     value: relatedAlerts.length,       color: relatedAlerts.length    ? '#ef4444' : '#34d399' },
                      ].map((s, i) => (
                        <VStack key={i} spacing={0} align="center" bg={statBoxBg} p={2} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="20px" fontWeight="700" color={s.color} fontFamily="mono">{s.value}</Text>
                          <Text fontSize="9px" color={mutedText} letterSpacing="0.05em" fontFamily="sans-serif">{s.label}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>

                    <Box borderRadius="xl" px={3} py={3} borderWidth="1px" borderColor="rgba(167,139,250,0.25)" bg={twinPromoBg}>
                      <HStack spacing={2} mb={1}>
                        <Icon as={FaRobot} color="#a78bfa" boxSize={3.5} />
                        <Text fontSize="10px" fontWeight="700" color="#a78bfa" fontFamily="sans-serif" letterSpacing="0.05em">DIGITAL TWIN READY</Text>
                      </HStack>
                      <Text fontSize="11px" color={mutedText} mb={2} lineHeight="1.6" fontFamily="sans-serif">Full predictive analytics, KPI tracking, simulation, and cascade modelling.</Text>
                      <Button size="sm" colorScheme="purple" variant="outline" leftIcon={<Icon as={FaRobot} />} onClick={() => (onOpenTwin ? onOpenTwin(enriched ?? asset) : onViewFullAsset?.(enriched ?? asset))} fontFamily="sans-serif" fontSize="11px" fontWeight="600">OPEN DIGITAL TWIN</Button>{/* --- UPDATE: twin drawer */}
                    </Box>

                    {!asset.isSensor && !asset.isPipeline && (
                      <Button size="md" colorScheme="blue" w="100%" onClick={() => onViewFullAsset?.(enriched ?? asset)} fontWeight="600" fontFamily="sans-serif" boxShadow="sm" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }} _active={{ boxShadow: 'sm', transform: 'translateY(0)' }} transition="all 0.2s" mt={2}>
                        View Detailed Analytics
                      </Button>
                    )}
                  </VStack>
                </MotionBox>
              </TabPanel>

              {/* ────────────────── TAB: SENSORS ────────────────── */}
              <TabPanel px={0} pb={0}>
                <MotionBox variants={STAGGER} initial="hidden" animate="visible">
                  <VStack spacing={3} align="stretch">
                    {isEnriching ? (
                      <Flex justify="center" py={6} gap={2} align="center">
                        <Spinner size="sm" color="blue.400" />
                        <Text fontSize="11px" color={mutedText} fontFamily="sans-serif">LOADING SENSORS…</Text>
                      </Flex>
                    ) : linkedSensors.length === 0 ? (
                      <Flex direction="column" align="center" py={8} gap={2}>
                        <Icon as={MdSensors} boxSize={8} color={mutedText} opacity={0.25} />
                        <Text fontSize="11px" color={mutedText} fontFamily="sans-serif" letterSpacing="0.05em">NO SENSORS LINKED</Text>
                      </Flex>
                    ) : (
                      <>
                        <HStack justify="space-between">
                          <Text fontSize="10px" color={mutedText} fontFamily="sans-serif" letterSpacing="0.05em">
                            {activeSensorCount} of {linkedSensors.length} REPORTING
                          </Text>
                          <Progress value={(activeSensorCount / linkedSensors.length) * 100} size="xs" colorScheme="green" borderRadius="full" w="80px" bg={progressBg} />
                        </HStack>
                        {linkedSensors.map(s => (
                          <MotionBox key={s.device_id ?? s.id} variants={FADE_UP}>
                            <SensorRow 
                              sensor={s} 
                              sensorReadings={sensorReadings}
                              psiConditions={psiConditions} 
                              isHistoricalMode={isHistoricalMode}
                              historicalTimestamp={historicalTimestamp}
                              // --- UPDATE
                              onOpenSensor={onOpenSensor}
                              intelRow={intel?.sensorByKey?.get(twinSensorKey(s)) || null}
                              historyOf={intel?.historyOf || null}
                              // --- END UPDATE
                            />
                          </MotionBox>
                        ))}
                      </>
                    )}
                  </VStack>
                </MotionBox>
              </TabPanel>

              {/* ────────────────── TAB: HEALTH ────────────────── */}
              <TabPanel px={0} pb={0}>
                <MotionBox variants={STAGGER} initial="hidden" animate="visible">
                  <VStack spacing={3} align="stretch">
                    {assetHealth ? (
                      <MotionBox variants={FADE_UP}><AssetHealthPanel assetHealth={assetHealth} riskScore={riskScore} liveStatus={liveStatus} /* --- UPDATE: + risk, live */ /></MotionBox>
                    ) : (
                      <Flex direction="column" align="center" py={6} gap={2}>
                        {riskScore?.score !== null && riskScore?.score !== undefined && <Text fontSize="xs" fontFamily="sans-serif">FlowSense risk {riskScore.score}/100 — see Overview for the reasons.</Text>}{/* --- UPDATE */}
                        <Icon as={MdHealthAndSafety} boxSize={8} color={mutedText} opacity={0.25} />
                        <Text fontSize="11px" color={mutedText} fontFamily="sans-serif" letterSpacing="0.05em">NO HEALTH DATA AVAILABLE</Text>
                      </Flex>
                    )}
                    {healthHistory.length >= 3 && (
                      <MotionBox variants={FADE_UP}><HealthTrendBar history={healthHistory} trend={healthTrend} /></MotionBox>
                    )}
                    <MotionBox variants={FADE_UP}>
                      <Button size="sm" w="100%" variant="outline" colorScheme="purple" leftIcon={<Icon as={FaCalendarAlt} />} onClick={() => setShowScheduler(v => !v)} fontFamily="sans-serif" fontSize="11px" fontWeight="600">
                        {showScheduler ? 'CLOSE SCHEDULER' : 'SCHEDULE MAINTENANCE'}
                      </Button>
                    </MotionBox>
                  </VStack>
                </MotionBox>
              </TabPanel>

              {/* ────────────────── TAB: ACTIVITY ────────────────── */}
              <TabPanel px={0} pb={0}>
                <MotionBox variants={STAGGER} initial="hidden" animate="visible">
                  <VStack spacing={3} align="stretch">
                    {relatedWorkOrders.length > 0 && (
                      <Expandable title="Work Orders" icon={FaTools} count={relatedWorkOrders.length} defaultOpen accentColor="#60a5fa">
                        <VStack spacing={2} align="stretch" pt={2}>
                          {relatedWorkOrders.map(wo => (
                            <Flex key={wo.id} bg={cardBg} p={2.5} borderRadius="lg" borderWidth="1px" borderColor={borderColor} justify="space-between" align="center" _hover={{ borderColor: woBdHov }} transition="border-color 0.2s">
                              <VStack align="start" spacing={0} maxW="66%" overflow="hidden">
                                <Text fontSize="11px" fontWeight="600" fontFamily="mono" noOfLines={1}>#{wo.id?.toString().substring(0, 8)}</Text>
                                <Text fontSize="11px" color={mutedText} noOfLines={1} fontFamily="sans-serif">{wo.title}</Text>
                              </VStack>
                              <HStack spacing={2}>
                                <Badge fontSize="10px" fontFamily="sans-serif" colorScheme={wo.priority === 'high' ? 'red' : wo.priority === 'medium' ? 'orange' : 'blue'}>{wo.priority?.toUpperCase()}</Badge>
                                {wo.id && (
                                  <Tooltip label="Mark resolved" hasArrow>
                                    <IconButton size="xs" variant="ghost" aria-label="Resolve" icon={<Icon as={FaCheckDouble} />} color="green.400" onClick={() => actionDispatch('RESOLVE_WORK_ORDER', { workOrderId: wo.id })} />
                                  </Tooltip>
                                )}
                              </HStack>
                            </Flex>
                          ))}
                        </VStack>
                      </Expandable>
                    )}
                    {relatedAlerts.length > 0 && (
                      <Expandable title="Active Alerts" icon={FaExclamationTriangle} count={relatedAlerts.length} defaultOpen accentColor="#ef4444">
                        <VStack spacing={2} align="stretch" pt={2}>
                          {relatedAlerts.map(alert => (
                            <Box key={alert.id} bg={alertBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={alertBd}>
                              <Flex justify="space-between" align="flex-start" mb={1}>
                                <HStack spacing={1}>
                                  <Icon as={MdNotifications} color={alertTxt} boxSize={3} />
                                  <Text fontSize="10px" fontWeight="700" color={alertTxt} textTransform="uppercase" fontFamily="sans-serif" letterSpacing="0.05em">{alert.severity || 'ALERT'}</Text>
                                </HStack>
                                {alert.id && (
                                  <Tooltip label="Acknowledge" hasArrow>
                                    <IconButton size="xs" variant="ghost" aria-label="Acknowledge" icon={<Icon as={MdCheck} />} color="green.400" onClick={() => actionDispatch('ACKNOWLEDGE_ALERT', { alertId: alert.id })} />
                                  </Tooltip>
                                )}
                              </Flex>
                              <Text fontSize="12px" color={alertTxt} lineHeight="1.5" wordBreak="break-word" fontFamily="sans-serif">{alert.message || alert.alert_message}</Text>
                            </Box>
                          ))}
                        </VStack>
                      </Expandable>
                    )}
                    {relatedIssues.length > 0 && (
                      <Expandable title="Reported Issues" icon={FaMicrochip} count={relatedIssues.length} accentColor="#f59e0b">
                        <VStack spacing={2} align="stretch" pt={2}>
                          {relatedIssues.map(issue => (
                            <Box key={issue.id} bg={issueBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={issueBd}>
                              <Text fontSize="12px" color={issueTxt} lineHeight="1.5" wordBreak="break-word" fontFamily="sans-serif">{issue.description}</Text>
                            </Box>
                          ))}
                        </VStack>
                      </Expandable>
                    )}
                    {totalActivity === 0 && (
                      <Flex direction="column" align="center" py={8} gap={2}>
                        <Icon as={FaCheckCircle} boxSize={7} color="green.400" opacity={0.5} />
                        <Text fontSize="11px" color={mutedText} fontFamily="sans-serif" letterSpacing="0.05em">ALL CLEAR — NO OPEN ACTIVITY</Text>
                      </Flex>
                    )}
                  </VStack>
                </MotionBox>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <AnimatePresence>
            {lastAction && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                <Flex align="center" gap={2} mt={3} px={3} py={2} borderRadius="lg" bg={lastAction.success ? 'rgba(52,211,153,0.07)' : 'rgba(239,68,68,0.07)'} borderWidth="1px" borderColor={lastAction.success ? 'rgba(52,211,153,0.22)' : 'rgba(239,68,68,0.22)'}>
                  <Icon as={lastAction.success ? FaCheckDouble : FaExclamationTriangle} color={lastAction.success ? '#34d399' : '#ef4444'} boxSize={3} />
                  <Text fontSize="10px" fontFamily="sans-serif" fontWeight="700" color={lastAction.success ? '#34d399' : '#ef4444'}>
                    {lastAction.action.replace(/_/g, ' ')} {lastAction.success ? '· SUCCESS' : `· FAILED: ${lastAction.error ?? `HTTP ${lastAction.statusCode}`}`}
                  </Text>
                </Flex>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </MotionBox>
    </AnimatePresence>
  );
};

export default AssetInformationPanel;